import React, { useState, useEffect } from 'react';
import { User, Shield, Bell, Home } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AccountPanel from './AccountPanel';
import SecurityPanel from './SecurityPanel';
import NotificationsPanel from './NotificationsPanel';
import toast from 'react-hot-toast';

type TabType = 'account' | 'security' | 'notifications';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as TabType;
    const checkout = params.get('checkout');

    if (tab && ['account', 'security', 'notifications'].includes(tab)) {
      setActiveTab(tab);
    }

    if (checkout === 'success') {
      toast.success('Thank you for your purchase! Your account has been upgraded.');
      params.delete('checkout');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location, navigate]);

  const tabs = [
    { id: 'account', name: 'Account', icon: User },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex items-center space-x-2">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <Home className="h-5 w-5 mr-2" />
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType);
                    const params = new URLSearchParams(location.search);
                    params.set('tab', tab.id);
                    navigate({ search: params.toString() });
                  }}
                  className={`
                    py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'account' && <AccountPanel />}
          {activeTab === 'security' && <SecurityPanel />}
          {activeTab === 'notifications' && <NotificationsPanel />}
        </div>
      </div>
    </div>
  );
}